/**
 * ============================================================================
 * LoginPage.tsx — 관리자 콘솔 로그인 화면
 * ============================================================================
 *
 * 【역할】
 *   사번/아이디 + 비밀번호 기반 JWT 로그인 화면. 좌측 브랜드 패널 + 우측 폼의
 *   2-pane 레이아웃이며, 데모 계정 빠른 입력 버튼을 제공한다. 인증 성공 후
 *   /auth/me 호출로 사용자 정보를 보강하고 정적 메뉴 트리를 시드한 뒤 대시보드로 이동.
 *
 * 【주요 기능】
 *   - Form 제출 → POST /auth/login → JWT 저장 → /auth/me 보강 → 메뉴 시드 → /dashboard
 *   - 데모 계정 3종(admin/manager/employee) 원클릭 입력
 *   - 실패 시 백엔드 표준 응답 error.message 우선, 폴백 메시지 표시
 *
 * 【Backend 호출】
 *   - POST /auth/login — useAuthStore.login() 내부 호출
 *   - GET  /auth/me   — useAuthStore.loadUserInfo() 로 user 객체 보강
 *
 * 【State】
 *   - loading: boolean   — 제출 진행 중 버튼 spinner 제어
 *   - errMsg : string    — Alert 표시용 사용자 메시지(빈 문자열이면 숨김)
 *   - form   : FormInst  — antd Form 인스턴스(데모 계정 채우기에 사용)
 *
 * 【사용처】 router/index.tsx — { path: '/login' }
 * 【포팅 원본】 (자체 작성)
 * 【의존성】 antd, @ant-design/icons, react-router-dom, zustand authStore
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, Form, Input, Button, Typography, Alert, Space, Divider, theme } from 'antd'
import {
  UserOutlined,
  LockOutlined,
  LoginOutlined,
  SafetyOutlined,
  ClockCircleOutlined
} from '@ant-design/icons'
import { useAuthStore } from '@/store/authStore'
import { seedDefaultMenus } from '@/layout/seedMenuStore'
import { useLabel } from '@/framework/useLabel'

const { Title, Paragraph, Text } = Typography

/** 데모 로그인 빠른 채우기용 계정 목록(개발/시연 전용 — 운영 배포 시 제거 검토). */
const DEMO_ACCOUNTS = [
  { id: 'admin', pw: 'admin123', labelKey: 'ROLE_ADMIN', labelKo: '관리자' },
  { id: 'manager', pw: 'manager123', labelKey: 'ROLE_PHARMACIST', labelKo: '약사' },
  { id: 'guardian', pw: 'guardian123', labelKey: 'LBL_GUARDIAN', labelKo: '보호자' }
]

export const LoginPage = () => {
  const navigate = useNavigate()
  const login = useAuthStore((s) => s.login)
  const loadUserInfo = useAuthStore((s) => s.loadUserInfo)
  const { token } = theme.useToken()
  const { t } = useLabel()
  const [loading, setLoading] = useState(false)
  const [errMsg, setErrMsg] = useState<string>('')
  const [form] = Form.useForm()

  /**
   * 폼 제출 핸들러 — 로그인 → 사용자 정보 로드 → 메뉴 시드 → 대시보드 이동.
   * 401/검증 실패 시 errMsg 갱신, 성공 시 navigate('/dashboard').
   * @param values - {loginId, password} 폼 값
   */
  const onFinish = async (values: { loginId: string; password: string }) => {
    setLoading(true)
    setErrMsg('')
    try {
      // ─── 1) 인증 ─ JWT 발급 후 store 에 저장
      const ok = await login(values.loginId, values.password)
      if (!ok) {
        setErrMsg(t('ERR_LOGIN_INVALID', '로그인 정보가 올바르지 않습니다'))
        return
      }
      // ─── 2) 사용자 정보 보강 ─ 백엔드 /auth/login 은 user 객체 미반환 → /auth/me 로 보강
      await loadUserInfo()
      // ─── 3) 메뉴 시드 ─ 정적 MENU_TREE 주입(백엔드 menus 미반환 보완)
      seedDefaultMenus()
      // ─── 4) 메인 화면 이동 ─
      navigate('/dashboard')
    } catch (e) {
      // 백엔드 표준 응답 {success:false, error:{code,message,...}} 의 message 우선 노출
      const err = (e as { response?: { data?: { error?: { message?: string } } } }).response?.data
        ?.error
      setErrMsg(err?.message || t('ERR_LOGIN_INVALID', '로그인 정보가 올바르지 않습니다'))
    } finally {
      setLoading(false)
    }
  }

  /**
   * 데모 계정 버튼 클릭 시 Form 필드를 즉시 채우는 헬퍼.
   * @param id - 사번/아이디
   * @param pw - 비밀번호(평문 — 데모 시연 한정)
   */
  const fillDemo = (id: string, pw: string) => {
    form.setFieldsValue({ loginId: id, password: pw })
  }

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        background: 'linear-gradient(135deg, #1677ff 0%, #722ed1 100%)'
      }}
    >
      {/* 좌측 브랜드 패널 */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 64,
          color: '#fff'
        }}
        className="brand-panel"
      >
        <ClockCircleOutlined style={{ fontSize: 96, marginBottom: 32, opacity: 0.9 }} />
        <Title level={1} style={{ color: '#fff', margin: 0 }}>
          {t('TITLE_APP_NAME', '복용 관리 시스템')}
        </Title>
        <Paragraph
          style={{
            color: 'rgba(255,255,255,0.85)',
            marginTop: 16,
            fontSize: 16,
            textAlign: 'center',
            maxWidth: 480
          }}
        >
          {t('SUBTITLE_APP', '노인 복용 디스펜서 + AI · IoT 통합 관제')}
        </Paragraph>
        <Space size="large" style={{ marginTop: 48, opacity: 0.85 }}>
          <Text style={{ color: '#fff' }}>
            <SafetyOutlined /> JWT + RBAC
          </Text>
          <Text style={{ color: '#fff' }}>Gemma Vision OCR + RAG</Text>
          <Text style={{ color: '#fff' }}>ko / en / vi i18n</Text>
        </Space>
      </div>

      {/* 우측 로그인 폼 */}
      <div
        style={{
          width: 480,
          background: token.colorBgContainer,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 40
        }}
      >
        <Card
          style={{ width: '100%', maxWidth: 360, border: 'none', boxShadow: 'none' }}
          styles={{ body: { padding: 0 } }}
        >
          <Title level={3} style={{ marginBottom: 4 }}>
            {t('BTN_LOGIN', '로그인')}
          </Title>
          <Paragraph type="secondary" style={{ marginBottom: 24 }}>
            {t('DESC_LOGIN', '계정 정보를 입력하여 관리자 콘솔에 접속하세요')}
          </Paragraph>

          {errMsg && (
            <Alert
              type="error"
              message={errMsg}
              showIcon
              style={{ marginBottom: 16 }}
              role="alert"
              data-testid="login-error"
            />
          )}

          <Form form={form} layout="vertical" onFinish={onFinish} size="large">
            <Form.Item
              name="loginId"
              label={t('LBL_LOGIN_ID', '사번/아이디')}
              rules={[{ required: true, message: t('MSG_INPUT_LOGIN_ID', '아이디를 입력하세요') }]}
            >
              <Input prefix={<UserOutlined />} placeholder="admin" autoComplete="username" />
            </Form.Item>
            <Form.Item
              name="password"
              label={t('LBL_PASSWORD', '비밀번호')}
              rules={[
                { required: true, message: t('MSG_INPUT_PASSWORD', '비밀번호를 입력하세요') }
              ]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="********"
                autoComplete="current-password"
              />
            </Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={loading}
              icon={<LoginOutlined />}
              style={{ height: 44, fontWeight: 600 }}
            >
              {t('BTN_LOGIN', '로그인')}
            </Button>
          </Form>

          <Divider style={{ marginBlock: 20 }} plain>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('LBL_DEMO_ACCOUNT', '데모 계정')}
            </Text>
          </Divider>

          <Space wrap size={8} style={{ width: '100%', justifyContent: 'center' }}>
            {DEMO_ACCOUNTS.map((acc) => (
              <Button key={acc.id} size="small" onClick={() => fillDemo(acc.id, acc.pw)}>
                {t(acc.labelKey, acc.labelKo)} ({acc.id})
              </Button>
            ))}
          </Space>

          <div style={{ marginTop: 24, textAlign: 'center' }}>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {t('TITLE_APP_NAME', '복용 관리 시스템')} v1.0 · FastAPI + PostgreSQL + React
            </Text>
          </div>
        </Card>
      </div>
    </div>
  )
}
